import type { Clip, Track } from '@/types'

export type MagneticMovePlan = { error: string } | { starts: Map<string, number> }

/** A single-track insertion move, carrying only explicitly linked companions. */
export function planMagneticMove(
  clips: ReadonlyMap<string, Clip>,
  tracks: ReadonlyMap<string, Track>,
  sequenceId: string,
  clipId: string,
  beforeClipId: string | null,
): MagneticMovePlan {
  const anchor = clips.get(clipId)
  if (!anchor || anchor.sequenceId !== sequenceId) return { error: 'The shot changed. Try dragging it again.' }
  const ordered = [...clips.values()]
    .filter(clip => clip.sequenceId === sequenceId && clip.trackIndex === anchor.trackIndex)
    .sort((a, b) => a.startOnTimeline - b.startOnTimeline || a.id.localeCompare(b.id))
  if (
    ordered.some(
      (clip, index) =>
        index > 0 && ordered[index - 1].startOnTimeline + ordered[index - 1].duration > clip.startOnTimeline,
    )
  ) {
    return { error: 'Separate overlapping shots on this track before rearranging them.' }
  }
  const rest = ordered.filter(clip => clip.id !== clipId)
  if (beforeClipId === clipId) return { starts: new Map() }
  const destination = beforeClipId ? rest.find(clip => clip.id === beforeClipId) : undefined
  if (beforeClipId && !destination) return { error: 'The destination changed. Try dragging again.' }
  const sourcePosition = ordered.findIndex(clip => clip.id === clipId)
  const destinationPosition = destination ? rest.findIndex(clip => clip.id === destination.id) : rest.length
  if (sourcePosition === destinationPosition) return { starts: new Map() }
  const afterRemoval = (clip: Clip) =>
    clip.startOnTimeline >= anchor.startOnTimeline + anchor.duration
      ? clip.startOnTimeline - anchor.duration
      : clip.startOnTimeline
  const insertion = destination
    ? afterRemoval(destination)
    : Math.max(0, ...rest.map(clip => afterRemoval(clip) + clip.duration))
  const starts = new Map<string, number>()
  starts.set(anchor.id, insertion)
  for (const clip of rest) {
    const start = afterRemoval(clip)
    starts.set(clip.id, start >= insertion ? start + anchor.duration : start)
  }
  for (const [id, start] of [...starts]) {
    const clip = clips.get(id)!
    if (!clip.linkGroupId) continue
    const delta = start - clip.startOnTimeline
    for (const companion of clips.values()) {
      if (companion.sequenceId !== sequenceId || companion.linkGroupId !== clip.linkGroupId || companion.id === id)
        continue
      const companionStart = companion.startOnTimeline + delta
      if (starts.has(companion.id) && starts.get(companion.id) !== companionStart) {
        return { error: 'This linked group cannot be rearranged together. Unlink the shots and try again.' }
      }
      starts.set(companion.id, companionStart)
    }
  }
  for (const [id, start] of starts) {
    const clip = clips.get(id)!
    if (start === clip.startOnTimeline) {
      starts.delete(id)
      continue
    }
    const track = [...tracks.values()].find(track => track.sequenceId === sequenceId && track.index === clip.trackIndex)
    if (!track || track.locked) return { error: 'An affected track is locked. Unlock it and try again.' }
    if (start < 0) return { error: 'Linked audio would move before the sequence starts. Choose another position.' }
    for (const other of clips.values()) {
      if (other.id === id || other.sequenceId !== sequenceId || other.trackIndex !== clip.trackIndex) continue
      const otherStart = starts.get(other.id) ?? other.startOnTimeline
      if (start < otherStart + other.duration && otherStart < start + clip.duration) {
        return { error: 'Linked audio would overlap another shot. Make room or unlink it before rearranging.' }
      }
    }
  }
  return { starts }
}
